// One step's results (spec §7/§8): a card per entry in `stats.steps`, in
// survey order, whose body depends on the step's kind. Choice and flat
// multi-select: one bar per option in the configured order, share = count ÷
// answered (a multi's shares can exceed 100% together — respondents pick
// several). Split multi-select: the two sets side by side, each scaled to
// its own busiest option and tinted by polarity. Text: the latest samples,
// with See all › opening the Responses page filtered to text.

import { Badge, Card } from "@pej/kore/components";
import { cloneElement, type ReactElement, type ReactNode } from "react";
import type { OptionCount, StepSummary, Survey, TextSample } from "@/api/types";
import { builtinIconSvg } from "@/features/surveys/choice-icons";
import { KIND_LABELS } from "@/features/surveys/step-kinds";
import { multiLabels, type OptionMetaEntry, optionMeta, stepById } from "./stats-mapping";

export interface StepSummaryCardProps {
    survey: Survey;
    summary: StepSummary;
    /** A link (to, params, search; no children) shown as See all › under a
     *  text step's samples; the card styles and labels it. */
    seeAll?: ReactElement<{ className?: string; children?: ReactNode }>;
}

function formatSubmittedAt(iso: string): string {
    const date = new Date(iso);
    return Number.isNaN(date.getTime()) ? iso : date.toLocaleString();
}

function OptionBars({
    options,
    answered,
    meta,
}: {
    options: OptionCount[];
    answered: number;
    meta: Map<string, OptionMetaEntry>;
}) {
    const countByKey = new Map(options.map((entry) => [entry.key, entry.count]));
    // Configured order first; an option the current config no longer knows
    // (deleted or renamed since some responses were submitted) is appended
    // rather than silently dropped, so the counts never quietly disagree.
    const orderedKeys = [...meta.keys(), ...options.map((entry) => entry.key).filter((key) => !meta.has(key))];
    if (orderedKeys.length === 0) {
        return <p className="mt-4 text-sm text-muted-foreground">No answers yet.</p>;
    }
    return (
        <div className="mt-4 flex flex-col gap-3">
            {orderedKeys.map((key) => {
                const count = countByKey.get(key) ?? 0;
                const info = meta.get(key) ?? { label: key, icon: null };
                const percent = answered > 0 ? Math.round((count / answered) * 100) : 0;
                const icon = info.icon ? builtinIconSvg(info.icon) : undefined;
                return (
                    <div key={key} className="flex items-center gap-3">
                        <span
                            aria-hidden="true"
                            className="flex size-5 shrink-0 items-center justify-center text-muted-foreground [&>svg]:size-4"
                            // biome-ignore lint/security/noDangerouslySetInnerHtml: static, module-authored SVG shapes (choice-icons.ts) — no user input reaches this markup.
                            dangerouslySetInnerHTML={icon ? { __html: icon } : undefined}
                        />
                        <span className="w-28 shrink-0 truncate text-sm">{info.label}</span>
                        <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                            <div
                                className="h-full rounded-full bg-primary"
                                style={{ width: `${Math.min(percent, 100)}%` }}
                            />
                        </div>
                        <span className="w-20 shrink-0 text-right text-sm tabular-nums text-muted-foreground">
                            {count} ({percent}%)
                        </span>
                    </div>
                );
            })}
        </div>
    );
}

function SplitColumn({
    title,
    options,
    labels,
    tone,
}: {
    title: string;
    options: OptionCount[];
    labels: Map<string, string>;
    tone: "negative" | "positive";
}) {
    const max = options.reduce((acc, option) => Math.max(acc, option.count), 0);
    const barClass = tone === "negative" ? "bg-kore-danger-text" : "bg-kore-success-text";
    const trackClass = tone === "negative" ? "bg-kore-danger-soft" : "bg-kore-success-soft";
    return (
        // min-w-0: without it a flex column won't shrink below its rows, and a
        // narrow card pushed the counts out past its edge with no room for bars.
        <div className="flex min-w-0 flex-1 flex-col gap-2">
            <h4 className="text-xs font-medium tracking-wide text-muted-foreground uppercase">{title}</h4>
            {options.length === 0 ? (
                <p className="text-sm text-muted-foreground">None yet.</p>
            ) : (
                options.map((option) => {
                    const percent = max > 0 ? Math.round((option.count / max) * 100) : 0;
                    return (
                        <div key={option.key} className="flex items-center gap-2 text-sm">
                            <span className="w-24 shrink-0 truncate">{labels.get(option.key) ?? option.key}</span>
                            <div className={`h-2 flex-1 overflow-hidden rounded-full ${trackClass}`}>
                                <div className={`h-full rounded-full ${barClass}`} style={{ width: `${percent}%` }} />
                            </div>
                            <span className="w-8 shrink-0 text-right tabular-nums text-muted-foreground">
                                {option.count}
                            </span>
                        </div>
                    );
                })
            )}
        </div>
    );
}

function TextSamples({ samples }: { samples: TextSample[] }) {
    if (samples.length === 0) {
        return <p className="mt-4 text-sm text-muted-foreground">No answers yet.</p>;
    }
    return (
        <ul className="mt-4 divide-y divide-border">
            {samples.map((sample) => (
                <li key={sample.responseId} className="flex items-baseline gap-3 py-2 text-sm">
                    <span className="min-w-0 flex-1 whitespace-pre-wrap">{sample.text}</span>
                    <span className="shrink-0 text-muted-foreground">{formatSubmittedAt(sample.submittedAt)}</span>
                </li>
            ))}
        </ul>
    );
}

export function StepSummaryCard({ survey, summary, seeAll }: StepSummaryCardProps) {
    const step = stepById(survey, summary.stepId);
    const body = summary.summary;
    const skipped = Math.max(summary.reached - summary.answered, 0);

    let content: ReactNode;
    if ("samples" in body) {
        content = (
            <>
                <TextSamples samples={body.samples} />
                {seeAll &&
                    cloneElement(
                        seeAll,
                        { className: "mt-3 inline-block text-sm text-kore-accent-text hover:underline" },
                        "See all ›",
                    )}
            </>
        );
    } else if ("negative" in body) {
        const labels = multiLabels(step);
        content = (
            // Side by side once the card has room for two sets of bars;
            // stacked below that (the card is a size container).
            <div className="mt-4 flex flex-col gap-4 @md:flex-row @md:gap-6">
                <SplitColumn title="Low scores" options={body.negative} labels={labels} tone="negative" />
                <SplitColumn title="High scores" options={body.positive} labels={labels} tone="positive" />
            </div>
        );
    } else {
        const meta =
            summary.kind === "choice"
                ? optionMeta(step)
                : new Map(
                      [...multiLabels(step)].map(([key, label]) => [key, { label, icon: null } as OptionMetaEntry]),
                  );
        content = <OptionBars options={body.options} answered={summary.answered} meta={meta} />;
    }

    return (
        <Card className="@container p-4" data-testid={`step-summary-${summary.position + 1}`}>
            <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 text-sm font-semibold">
                    {summary.position + 1}. {summary.prompt || KIND_LABELS[summary.kind]}
                </h3>
                <Badge tone="neutral" className="shrink-0 whitespace-nowrap">
                    {KIND_LABELS[summary.kind]}
                </Badge>
            </div>
            <p className="mt-1 text-xs text-muted-foreground tabular-nums">
                Reached {summary.reached} · Answered {summary.answered} · Skipped {skipped}
            </p>
            {content}
        </Card>
    );
}
